import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { CircularProgress } from '@mui/material';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { beginPdfPageCanvasRender } from '../helpers/pdf-rasterize.helpers';
import {
  cancelPdfRenderTask,
  isPdfRenderingCancelled,
} from '../helpers/pdf-page-cleanup.helpers';

type PdfPageLivePreviewProps = {
  doc: PDFDocumentProxy;
  pageIndex: number;
  className?: string;
  canvasClassName?: string;
};

/**
 * Live pdf.js render of one page into a canvas sized to the pane.
 * View-only — does not produce a stored JPEG.
 */
export const PdfPageLivePreview: React.FC<PdfPageLivePreviewProps> = ({
  doc,
  pageIndex,
  className,
  canvasClassName,
}) => {
  const { classes, cx } = useStyles();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const hasPaintedRef = useRef(false);

  // Reset first-paint spinner when the page changes.
  useEffect(() => {
    hasPaintedRef.current = false;
  }, [doc, pageIndex]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    let cancelled = false;
    let frame = 0;
    let paintGeneration = 0;
    let activeTask: RenderTask | undefined;

    const paint = () => {
      const cssWidth = wrap.clientWidth;
      const cssHeight = wrap.clientHeight;
      if (!(cssWidth > 0) || !(cssHeight > 0)) return;

      const generation = ++paintGeneration;
      // Overlapping renders on one canvas flip/mirror the page — cancel first.
      if (activeTask) cancelPdfRenderTask(activeTask);
      activeTask = undefined;
      // Full-screen spinner only on the first paint for this page.
      if (!hasPaintedRef.current) setLoading(true);

      void beginPdfPageCanvasRender(doc, pageIndex, canvas, {
        cssWidth,
        cssHeight,
        devicePixelRatio: window.devicePixelRatio || 1,
      })
        .then(async (task) => {
          if (cancelled || generation !== paintGeneration) {
            cancelPdfRenderTask(task);
            return;
          }
          activeTask = task;
          try {
            await task.promise;
          } catch (error: unknown) {
            if (isPdfRenderingCancelled(error)) return;
            throw error;
          }
          if (cancelled || generation !== paintGeneration) return;
          hasPaintedRef.current = true;
          setLoading(false);
        })
        .catch((error: unknown) => {
          if (isPdfRenderingCancelled(error)) return;
          if (!cancelled && generation === paintGeneration) {
            setLoading(false);
          }
        });
    };

    const schedulePaint = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(paint);
    };

    schedulePaint();
    const observer = new ResizeObserver(schedulePaint);
    observer.observe(wrap);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (activeTask) cancelPdfRenderTask(activeTask);
      observer.disconnect();
    };
  }, [doc, pageIndex]);

  return (
    <div ref={wrapRef} className={cx(classes.wrap, className)}>
      <canvas ref={canvasRef} className={cx(classes.canvas, canvasClassName)} />
      {loading && (
        <div className={classes.loading}>
          <CircularProgress size={22} />
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  wrap: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  canvas: {
    // Keep the exact CSS size set by pdf.js fit — max-width/height can squash
    // width and height independently and distort the page aspect ratio.
    display: 'block',
    flexShrink: 0,
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
  },
  loading: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.35)',
    pointerEvents: 'none',
  },
}));
